import { type MutationKey, type QueryKey } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { summarizeError } from '../utils/sanitize-log-meta';

export type QueryErrorKind = 'query' | 'mutation';

/**
 * Safe summary of a failed query or mutation. This is what reaches a
 * monitoring sink, so it must never carry headers, request/response bodies or
 * mutation variables — the raw axios error has all three.
 */
export interface QueryErrorRecord {
  kind: QueryErrorKind;
  /** The query/mutation key with object entries reduced to their field names. */
  key: string;
  message: string;
  status?: number;
  method?: string;
  /** Request path without the query string. */
  path?: string;
  /** Server error code (`response.data.code`) when the API returned one. */
  code?: string;
}

/**
 * Receives every unexpected failure once, after retries are exhausted. `error`
 * is the raw value for sinks that want it (the dev console); production
 * reporters should send `record` only.
 */
export type QueryErrorReporter = (record: QueryErrorRecord, error: unknown) => void;

const HTTP_UNAUTHORIZED = 401;

/**
 * Failures that happen in normal operation and are handled elsewhere:
 * - 401: the api-client refreshes and retries, and a failed refresh logs out;
 * - no response: offline, timeout or DNS — there is nothing to fix in the app.
 * Everything else (4xx, 5xx, thrown errors in a queryFn) is reported.
 *
 * Cancellations are not listed because they never reach a cache `onError`:
 * query-core returns or rethrows on the `silent` and `revert` flags earlier in
 * `Query#fetch`, and mutations cannot be cancelled at all.
 */
export const isExpectedQueryError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  if (!error.response) return true;
  return error.response.status === HTTP_UNAUTHORIZED;
};

const describeKeyPart = (part: unknown): string => {
  if (part == null) return String(part);
  if (typeof part !== 'object') return String(part);
  if (Array.isArray(part)) return `[${part.map(describeKeyPart).join(',')}]`;
  return `{${Object.keys(part).sort().join(',')}}`;
};

const describeKey = (key: QueryKey | MutationKey | undefined): string =>
  key ? `[${key.map(describeKeyPart).join(',')}]` : '';

// String-only on purpose: React Native's `URL` is a partial polyfill whose
// `pathname` is not in the RN type surface and is unreliable at runtime.
const pathOf = ({ url }: { url?: string }): string | undefined => {
  if (!url) return undefined;
  const [withoutQuery = ''] = url.split(/[?#]/);
  const afterScheme = withoutQuery.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '');
  const firstSlash = afterScheme.indexOf('/');
  // Absolute URL: drop the host. Relative path: keep it as written.
  if (afterScheme !== withoutQuery) {
    return firstSlash === -1 ? '/' : afterScheme.slice(firstSlash);
  }
  return withoutQuery;
};

const messageOf = (error: unknown): string => {
  if (typeof error === 'string') return error;
  const message = (error as { message?: unknown } | null)?.message;
  return typeof message === 'string' ? message : String(error);
};

export const describeQueryError = (
  kind: QueryErrorKind,
  key: QueryKey | MutationKey | undefined,
  error: unknown,
): QueryErrorRecord => {
  const record: QueryErrorRecord = { kind, key: describeKey(key), message: messageOf(error) };
  if (!isAxiosError(error)) return record;
  const { status, code } = summarizeError(error);
  if (status !== undefined) record.status = status;
  if (typeof code === 'string') record.code = code;
  if (error.config) {
    record.method = error.config.method?.toUpperCase();
    record.path = pathOf(error.config);
  }
  return record;
};

/** Development reporter: the summary plus the raw error, in the dev console. */
export const logQueryErrorToConsole: QueryErrorReporter = (record, error) =>
  console.error(`[react-query ${record.kind}]`, record, error);

/**
 * Wraps a host reporter with the filter-then-summarise policy, so the query
 * client only has to adapt the two cache callback signatures.
 *
 * The reporter is called inside query-core's `catch` in `Query#fetch`; a throw
 * there would replace the original rejection and skip `onSettled` (the
 * mutation side try/catches, the query side does not), so it is guarded here.
 */
export const createQueryErrorHandler =
  (onError: QueryErrorReporter) =>
  (kind: QueryErrorKind, key: QueryKey | MutationKey | undefined, error: unknown): void => {
    if (isExpectedQueryError(error)) return;
    try {
      onError(describeQueryError(kind, key, error), error);
    } catch {
      // A failing reporter must not change what the caller sees.
    }
  };
